import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, AlertTriangle, ExternalLink, CheckCircle2 } from "lucide-react";
import { formatDate } from "@/lib/utils";

const getLogSender = (log, language) => {
  const sender = log.user_email || log.context?.userEmail || log.username;
  return sender || (language === "es" ? "Usuario anónimo" : "Anonymous user");
};

const getActionSummary = (lastAction, language) => {
  if (!lastAction) return language === "es" ? "hacer una acción no identificada" : "perform an unidentified action";

  const actionText = String(lastAction.text || "").trim();
  const actionTarget = lastAction.testId || lastAction.tagName || (language === "es" ? "la interfaz" : "the interface");

  if (actionText) {
    return language === "es"
      ? `interactuar con “${actionText}”`
      : `interact with “${actionText}”`;
  }

  return language === "es"
    ? `interactuar con ${actionTarget}`
    : `interact with ${actionTarget}`;
};

const getApiSummary = (lastApi, language) => {
  if (!lastApi?.url) return language === "es" ? "No quedó una API fallida registrada antes del error." : "No failed API was captured before the error.";
  const method = lastApi.method || "API";
  const status = lastApi.status || "ERR";
  const message = lastApi.message ? ` · ${lastApi.message}` : "";
  return `${method} ${status} ${lastApi.url}${message}`;
};

const getMailboxSubject = (log, language) => {
  const route = log.route_path || log.context?.routePath || "/";
  const actionSummary = getActionSummary(log.last_action, language);
  const fallbackMessage = log.message || log.title || (language === "es" ? "Error sin mensaje" : "Error without message");

  if (log.last_action) {
    return language === "es"
      ? `Falló la app cuando el usuario intentó ${actionSummary} en ${route}`
      : `The app failed when the user tried to ${actionSummary} on ${route}`;
  }

  if (log.last_api_request?.url) {
    return language === "es"
      ? `Se registró un error en ${route} relacionado con ${log.last_api_request.method || "API"}`
      : `An error was captured on ${route} related to ${log.last_api_request.method || "API"}`;
  }

  return fallbackMessage;
};

const getMailboxPreview = (log, language) => {
  const browserLanguage = log.browser_info?.language || (language === "es" ? "idioma no detectado" : "language not detected");
  const viewport = log.browser_info?.viewport || (language === "es" ? "viewport no detectado" : "viewport not detected");
  const apiSummary = getApiSummary(log.last_api_request, language);
  const baseMessage = log.message || log.title || (language === "es" ? "Sin mensaje de error" : "No error message");
  const repeatSummary = (log.occurrence_count || 1) > 1
    ? (language === "es" ? ` · Repetido ${log.occurrence_count} veces` : ` · Repeated ${log.occurrence_count} times`)
    : "";

  return language === "es"
    ? `${baseMessage}${repeatSummary} · Última API: ${apiSummary} · Navegador: ${browserLanguage} · Vista: ${viewport}`
    : `${baseMessage}${repeatSummary} · Last API: ${apiSummary} · Browser: ${browserLanguage} · View: ${viewport}`;
};

const getMailboxNarrative = (log, language) => {
  const sender = getLogSender(log, language);
  const route = log.route_path || log.context?.routePath || (language === "es" ? "ruta no detectada" : "unknown route");
  const actionSummary = getActionSummary(log.last_action, language);
  const apiSummary = getApiSummary(log.last_api_request, language);
  const browserLanguage = log.browser_info?.language || (language === "es" ? "no detectado" : "not detected");
  const viewport = log.browser_info?.viewport || (language === "es" ? "no detectado" : "not detected");
  const coreMessage = log.message || log.title || (language === "es" ? "Sin mensaje de error" : "No error message");

  return language === "es"
    ? `${sender} estaba usando ${route} cuando la app reportó este problema: ${coreMessage}. La última acción capturada fue ${actionSummary}. ${apiSummary} Navegador/idioma: ${browserLanguage}. Viewport: ${viewport}.`
    : `${sender} was on ${route} when the app reported this issue: ${coreMessage}. The last captured action was ${actionSummary}. ${apiSummary} Browser/language: ${browserLanguage}. Viewport: ${viewport}.`;
};

export const ErrorLogsPanel = ({
  logs,
  loading,
  language,
  unseenTotal,
  onMarkSeen,
  onMarkAllSeen,
  onDeleteSelected,
  deletingSelected,
}) => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedLogIds, setSelectedLogIds] = useState([]);

  useEffect(() => {
    const availableIds = new Set(logs.map((log) => log.log_id));
    setSelectedLogIds((prev) => prev.filter((id) => availableIds.has(id)));
    if (selectedLog && !availableIds.has(selectedLog.log_id)) {
      setSelectedLog(null);
    }
  }, [logs, selectedLog]);

  const allSelected = logs.length > 0 && selectedLogIds.length === logs.length;

  const toggleLogSelection = (logId, checked) => {
    setSelectedLogIds((prev) => {
      if (checked) {
        return prev.includes(logId) ? prev : [...prev, logId];
      }
      return prev.filter((id) => id !== logId);
    });
  };

  const toggleSelectAll = (checked) => {
    setSelectedLogIds(checked ? logs.map((log) => log.log_id) : []);
  };

  const handleDeleteSelected = async () => {
    if (!selectedLogIds.length) return;
    const deletedIds = [...selectedLogIds];
    await onDeleteSelected(deletedIds);
    setSelectedLogIds((prev) => prev.filter((id) => !deletedIds.includes(id)));
    if (selectedLog && deletedIds.includes(selectedLog.log_id)) {
      setSelectedLog(null);
    }
  };

  const copy = useMemo(() => ({
    title: language === "es" ? "Buzón de errores" : "Error mailbox",
    empty: language === "es" ? "Todavía no hay mensajes de error." : "There are no error messages yet.",
    markSeen: language === "es" ? "Marcar como visto" : "Mark as seen",
    markAllSeen: language === "es" ? "Marcar todos como vistos" : "Mark all as seen",
    details: language === "es" ? "Abrir mensaje" : "Open message",
    new: language === "es" ? "Nuevo" : "New",
    seen: language === "es" ? "Leído" : "Read",
    browser: language === "es" ? "Navegador" : "Browser",
    route: language === "es" ? "Ruta" : "Route",
    lastAction: language === "es" ? "Última acción" : "Last action",
    lastApi: language === "es" ? "Última API fallida" : "Last failed API",
    context: language === "es" ? "Contexto" : "Context",
    stack: language === "es" ? "Stack" : "Stack",
    summary: language === "es" ? "Resumen claro" : "Plain-language summary",
    technical: language === "es" ? "Detalle técnico" : "Technical detail",
    selectAll: language === "es" ? "Seleccionar todos" : "Select all",
    selectedCount: language === "es" ? "seleccionados" : "selected",
    deleteSelected: language === "es" ? "Eliminar seleccionados" : "Delete selected",
    repeated: language === "es" ? "Repetido" : "Repeated",
  }), [language]);

  if (loading) {
    return (
      <Card className="bg-card border-border p-8" data-testid="admin-error-logs-loading-state">
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4" data-testid="admin-error-logs-panel">
        <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold">{copy.title}</h3>
            <p className="text-sm text-muted-foreground">
              {language === "es"
                ? "Cada error se muestra como un mensaje con contexto completo para entender qué hizo el usuario y qué se rompió."
                : "Each error is shown like a message with full context so you can understand what the user did and what broke."}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-2" data-testid="admin-error-logs-select-all-row">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked) => toggleSelectAll(Boolean(checked))}
                data-testid="admin-error-logs-select-all-checkbox"
              />
              <span className="text-sm text-muted-foreground">{copy.selectAll}</span>
            </div>
            <Button
              variant="outline"
              onClick={onMarkAllSeen}
              disabled={!unseenTotal}
              data-testid="admin-error-logs-mark-all-seen-button"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {copy.markAllSeen}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteSelected}
              disabled={!selectedLogIds.length || deletingSelected}
              data-testid="admin-error-logs-delete-selected-button"
            >
              {deletingSelected ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {copy.deleteSelected}
            </Button>
          </div>
        </div>

        {selectedLogIds.length > 0 && (
          <Card className="border-border bg-card p-3" data-testid="admin-error-logs-selection-summary">
            <p className="text-sm text-muted-foreground">
              {selectedLogIds.length} {copy.selectedCount}
            </p>
          </Card>
        )}

        {logs.length === 0 ? (
          <Card className="bg-card border-border p-8 text-center text-muted-foreground" data-testid="admin-error-logs-empty-state">
            {copy.empty}
          </Card>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => (
              <Card
                key={log.log_id}
                className={`border-border p-0 overflow-hidden ${log.status === "new" ? "ring-1 ring-red-500/40" : ""}`}
                data-testid={`admin-error-log-card-${log.log_id}`}
              >
                <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedLogIds.includes(log.log_id)}
                      onCheckedChange={(checked) => toggleLogSelection(log.log_id, Boolean(checked))}
                      className="mt-1"
                      data-testid={`admin-error-log-checkbox-${log.log_id}`}
                    />
                  </div>
                  <div className="min-w-0 flex-1 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold break-all" data-testid={`admin-error-log-sender-${log.log_id}`}>
                          {getLogSender(log, language)}
                        </p>
                        <p className="text-xs text-muted-foreground" data-testid={`admin-error-log-date-${log.log_id}`}>
                          {formatDate(log.created_at)}
                        </p>
                      </div>
                      <Badge className={log.status === "new" ? "bg-red-500 text-white" : "bg-muted text-foreground"} data-testid={`admin-error-log-status-${log.log_id}`}>
                        {log.status === "new" ? copy.new : copy.seen}
                      </Badge>
                      {(log.occurrence_count || 1) > 1 && (
                        <Badge variant="outline" data-testid={`admin-error-log-repeat-count-${log.log_id}`}>
                          {copy.repeated} ×{log.occurrence_count}
                        </Badge>
                      )}
                    </div>
                    <div>
                      <p className="font-semibold break-words" data-testid={`admin-error-log-subject-${log.log_id}`}>
                        {getMailboxSubject(log, language)}
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground break-words" data-testid={`admin-error-log-preview-${log.log_id}`}>
                        {getMailboxPreview(log, language)}
                      </p>
                    </div>
                    <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                      <p data-testid={`admin-error-log-route-${log.log_id}`}>{copy.route}: {log.route_path || "—"}</p>
                      <p data-testid={`admin-error-log-action-${log.log_id}`}>{copy.lastAction}: {getActionSummary(log.last_action, language)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 lg:justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedLog(log);
                        if (log.status === "new") onMarkSeen([log.log_id]);
                      }}
                      data-testid={`admin-error-log-view-button-${log.log_id}`}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {copy.details}
                    </Button>
                    {log.status === "new" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onMarkSeen([log.log_id])}
                        data-testid={`admin-error-log-mark-seen-button-${log.log_id}`}
                      >
                        {copy.markSeen}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-4xl" data-testid="admin-error-log-detail-dialog">
          {selectedLog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  {getMailboxSubject(selectedLog, language)}
                </DialogTitle>
                <DialogDescription>
                  {getLogSender(selectedLog, language)} · {formatDate(selectedLog.created_at)} · {selectedLog.route_path || "—"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 text-sm">
                <Card className="p-4">
                  <p className="font-semibold" data-testid="admin-error-log-detail-summary-title">{copy.summary}</p>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground" data-testid="admin-error-log-detail-summary-body">
                    {getMailboxNarrative(selectedLog, language)}
                  </p>
                </Card>
                <Card className="p-4">
                  <p className="font-semibold">{copy.lastAction}</p>
                  <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground">{JSON.stringify(selectedLog.last_action || {}, null, 2)}</pre>
                </Card>
                <Card className="p-4">
                  <p className="font-semibold">{copy.lastApi}</p>
                  <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground">{JSON.stringify(selectedLog.last_api_request || {}, null, 2)}</pre>
                </Card>
                <Card className="p-4">
                  <p className="font-semibold">{copy.browser}</p>
                  <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground">{JSON.stringify(selectedLog.browser_info || {}, null, 2)}</pre>
                </Card>
                <Card className="p-4">
                  <p className="font-semibold">{copy.context}</p>
                  <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground">{JSON.stringify(selectedLog.context || {}, null, 2)}</pre>
                </Card>
                <Card className="p-4">
                  <p className="font-semibold">{copy.technical}</p>
                  <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground">{selectedLog.stack || "—"}</pre>
                </Card>
                <Card className="p-4">
                  <p className="font-semibold">{copy.stack}</p>
                  <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground">{selectedLog.stack || "—"}</pre>
                </Card>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};